import React, { useEffect, useState } from "react";
import { CreateAlbumDto } from "../types/album";
import OverlayRoot from "./shared/OverlayRoot";
import JoinAlbumPanel from "./JoinAlbumPanel";

type AlbumModalTab = "create" | "join";

interface CreateAlbumModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (albumData: CreateAlbumDto) => Promise<void>;
  onJoined?: (album: { ownerId: string; albumId: string }) => void;
  initialTab?: AlbumModalTab;
  isLoading?: boolean;
}

const CreateAlbumModal: React.FC<CreateAlbumModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onJoined,
  initialTab = "create",
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<AlbumModalTab>(initialTab);
  const [formData, setFormData] = useState<CreateAlbumDto>({
    name: "",
    description: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Album name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Album name must be at least 2 characters";
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Album name must be less than 100 characters";
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = "Description must be less than 500 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: CreateAlbumDto = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
      };

      await onSubmit(submitData);

      // Reset form on successful submission
      setFormData({ name: "", description: "" });
      setErrors({});
      onClose();
    } catch (error) {
      console.error("Error creating album:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({ name: "", description: "" });
      setErrors({});
      setActiveTab("create");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <OverlayRoot data-section="create-album-modal-root" className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-20 pb-4 overscroll-none">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-full transform transition-all duration-300 scale-100 opacity-100">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 overflow-hidden max-h-full flex flex-col">
          {/* Background Glow */}
          <div className="absolute inset-0 jp-gradient-brand-soft rounded-3xl opacity-80"></div>

          {/* Header */}
          <div className="relative z-10 px-6 py-4 border-b border-white/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 jp-icon-blue rounded-2xl flex items-center justify-center shadow-2xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={
                        activeTab === "join"
                          ? "M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                          : "M12 6v6m0 0v6m0-6h6m-6 0H6"
                      }
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{activeTab === "join" ? "Join Album" : "Create Album"}</h2>
                  <p className="text-sm text-white/70">
                    {activeTab === "join" ? "Add a shared album from an invite link" : "Start organizing your memories"}
                  </p>
                </div>
              </div>

              <button
                onClick={handleClose}
                disabled={isLoading}
                className="group relative z-50 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center backdrop-blur-sm hover:scale-110 hover:rotate-90"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="relative z-10 px-6 pt-4">
            <div role="tablist" aria-label="Album actions" className="flex gap-2 p-1 rounded-2xl bg-white/10">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "create"}
                onClick={() => setActiveTab("create")}
                disabled={isLoading}
                className={`jp-pill flex-1 justify-center ${activeTab === "create" ? "is-active" : ""}`}
              >
                Create
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "join"}
                onClick={() => setActiveTab("join")}
                disabled={isLoading}
                className={`jp-pill flex-1 justify-center ${activeTab === "join" ? "is-active" : ""}`}
              >
                Join
              </button>
            </div>
          </div>

          {activeTab === "join" ? (
            <div className="relative z-10 p-6 flex-1 overflow-y-auto overscroll-contain">
              <JoinAlbumPanel
                isOpen={isOpen && activeTab === "join"}
                disabled={isLoading}
                onJoined={(album) => {
                  handleClose();
                  onJoined?.(album);
                }}
              />
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="relative z-10 p-6 space-y-4 flex-1 overflow-y-auto overscroll-contain">
            {/* Album Name */}
            <div className="space-y-2">
              <label htmlFor="name" className="block text-sm font-semibold text-white">
                Album Name *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className={`
                    w-full px-4 py-3 text-lg font-medium text-white
                    bg-white/10 border-2 rounded-2xl backdrop-blur-sm
                    focus:outline-none focus:ring-4 transition-all duration-300
                    disabled:bg-white/5 disabled:cursor-not-allowed
                    placeholder-gray-300
                    ${
                      errors.name
                        ? "border-red-400 focus:border-red-500 focus:ring-red-500/20"
                        : "border-white/30 focus:border-[rgba(var(--primary-blue-rgb),0.55)] focus:ring-[rgba(var(--primary-blue-rgb),0.18)] hover:border-white/40"
                    }
                  `}
                  placeholder="My Amazing Album"
                  maxLength={100}
                />

                {/* Character count */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-xs text-gray-300 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                    {formData.name.length}/100
                  </span>
                </div>
              </div>
              {errors.name && (
                <div className="flex items-center gap-2 text-red-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <span className="text-sm font-medium">{errors.name}</span>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="description" className="block text-sm font-semibold text-white">
                Description <span className="text-gray-300 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  rows={3}
                  className={`
                    w-full px-4 py-3 text-base text-white
                    bg-white/10 border-2 rounded-2xl resize-none backdrop-blur-sm
                    focus:outline-none focus:ring-4 transition-all duration-300
                    disabled:bg-white/5 disabled:cursor-not-allowed
                    placeholder-gray-300
                    ${
                      errors.description
                        ? "border-red-400 focus:border-red-500 focus:ring-red-500/20"
                        : "border-white/30 focus:border-[rgba(var(--primary-blue-rgb),0.55)] focus:ring-[rgba(var(--primary-blue-rgb),0.18)] hover:border-white/40"
                    }
                  `}
                  placeholder="Tell the story of this album..."
                  maxLength={500}
                />

                {/* Character count */}
                <div className="absolute right-3 bottom-3">
                  <span
                    className={`text-xs px-2 py-1 rounded-full backdrop-blur-sm transition-colors ${
                      (formData.description?.length || 0) > 450
                        ? "text-orange-300 bg-orange-500/20"
                        : "text-gray-300 bg-white/20"
                    }`}
                  >
                    {formData.description?.length || 0}/500
                  </span>
                </div>
              </div>

              {errors.description && (
                <div className="flex items-center gap-2 text-red-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <span className="text-sm font-medium">{errors.description}</span>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="jp-btn jp-btn--secondary flex-1"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isLoading || !formData.name.trim()}
                className="jp-btn jp-btn--primary flex-1"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Creating Album...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <span>Create Album</span>
                  </>
                )}
              </button>
            </div>

          </form>
          )}
        </div>
      </div>
    </OverlayRoot>
  );
};

export default CreateAlbumModal;
